'use client';

import { useSelector } from 'react-redux';

import { projectApi } from '@highlight-cp/project-api';
import { userSliceSelectors } from '@highlight-cp/redux-state';

import { isImpersonatingSession, useImpersonationSession, ROLES } from '../impersonation-session';

interface UseGetCurrentUserOptions {
  skip?: boolean;
}

const useGetCurrentUser = ({ skip = false }: UseGetCurrentUserOptions = {}) => {
  const session = useImpersonationSession();
  const isImpersonating = isImpersonatingSession(session);
  const impersonationEmail = isImpersonating ? session.impersonation_target.email.trim() : '';
  const id = useSelector(userSliceSelectors.id);
  const sessionUserId = Number(session?.user_id);
  const hasValidSessionUserId = Number.isSafeInteger(sessionUserId) && sessionUserId > 0;
  const hasMatchingUserId = hasValidSessionUserId && id === sessionUserId;
  const shouldSkipById = skip || !session || isImpersonating || !hasMatchingUserId;
  const shouldSkipByEmail = skip || !isImpersonating || !impersonationEmail;
  const customerId = shouldSkipById ? -1 : sessionUserId;
  const hasAdminRole = session?.role === ROLES.ADMIN; // This is App Role, Not Organization Role

  // NOT IMPERSONATING: fetch the current user by ID
  const {
    data,
    error: errorById,
    isLoading: isLoadingById,
  } = projectApi.useGetCustomersByCustomerIdQuery(
    { customerId },
    {
      skip: shouldSkipById,
      // Force refetch when id changes from null to a value
      refetchOnMountOrArgChange: true,
    },
  );

  // IMPERSONATING: fetch the current user by email
  const {
    data: customers,
    error: errorByEmail,
    isLoading: isLoadingCustomers,
  } = projectApi.useGetCustomersQuery(
    { emailAddress: impersonationEmail },
    { skip: shouldSkipByEmail },
  );

  const currentUser = isImpersonating ? customers?.[0] : data;

  return {
    data: currentUser,
    error: errorById ?? errorByEmail,
    hasAdminRole,
    isImpersonating,
    impersonating: isImpersonating ? session.impersonation_target : undefined,
    isLoading: isLoadingById || isLoadingCustomers,
  };
};

export default useGetCurrentUser;
